import _ from 'underscore';
import { CampaignType as AccountLevelCampaignType } from '@bingads-webui-campaign/campaign-ui-common';
import { CardSize } from '@bingads-webui-campaign-react/overview-card-const';
import edm from '@bingads-webui-component-legacy/edm-campaign/index';

import {
  cardWelcomeTipsId,
  cardPerformanceId,
  cardDiagnosticId,
  cardAssetPerformanceId,
  cardRecommendationsId,
  cardTopCampaignsId,
  cardTopAccountsId,
  cardTopKeywordsId,
  cardDeviceId,
  cardSearchTermsId,
  cardCompetitionId,
  cardTopAdGroupsId,
  cardConversionTrackingId,
  cardTopLocationsId,
  cardTopChangesAccountId,
  cardTopChangesCampaignId,
  cardTopChangesAdGroupId,
  cardNavigateToFullProductId,
} from '@bingads-webui-campaign/fluent-page-consts';

import {
  getDfmPreferenceObjectForPerformanceTable,
  getDefaultPerformanceTablePreference,
  isTablePreferenceValid,
} from '../preference/card-preference/performance-table';

import {
  getDefaultPerformanceChartPreference,
  isPerformanceChartPreferenceValid,
} from '../preference/card-preference/performance-chart';

import {
  getDfmPreferenceObjectForTopChanges,
  getDefaultTopChangesPreference,
} from '../preference/card-preference/top-changes';

import {
  getDefaultDevicePreference,
  isDevicePreferenceValid,
} from '../preference/card-preference/devices';

const CampaignLevelCampaignType = _.mapObject(AccountLevelCampaignType, (val, key) => key);

function getCardOptions({
  navigationLevel,
  campaignType,
  isKeyword = false,
  isSearchTerm = false,
}) {
  let edmLevel;
  let entitySet;
  let targetNagivationLevel = navigationLevel;
  let extraFilter;

  switch (navigationLevel) {
    case 'account':
      edmLevel = edm.currentAccount;
      entitySet = edmLevel.Campaigns;
      break;
    case 'campaign':
      // For PMax and App campaign, call campaigns on account level with filter by Campaign Id to include BI data for both AssetGroups and AdGroups before migration.
      if (campaignType === CampaignLevelCampaignType.PerformanceMax || campaignType === CampaignLevelCampaignType.App) {
        targetNagivationLevel = 'account';
        edmLevel = edm.currentAccount;
        entitySet = edmLevel.Campaigns;
        extraFilter = [{
          filter: {
            key: 'CampaignId',
            value: {
              query: {
                Id: _.result(edm.currentCampaign, ['navigation', 'key']),
              },
            },
          },
        }];
      } else {
        edmLevel = edm.currentCampaign;
        entitySet = edmLevel.AdGroups;
      }
      break;
    case 'customer':
      edmLevel = edm.currentCustomer;
      entitySet = edmLevel.Accounts;
      break;
    default:
      edmLevel = undefined;
  }

  if (isKeyword) {
    return {
      entitySet: edmLevel.Keywords,
      navigationLevel,
    };
  }

  if (isSearchTerm) {
    return {
      entitySet: edmLevel.SearchTerms,
      navigationLevel,
    };
  }

  return {
    entitySet,
    navigationLevel: targetNagivationLevel,
    extraFilter,
  };
}

function getCustomerLevelDataEntitySetOptions() {
  return getCardOptions({
    navigationLevel: 'customer',
  });
}

function getAccountLevelDataEntitySetOptions() {
  return getCardOptions({
    navigationLevel: 'account',
  });
}

function getTopKeywordCardOptions(navigationLevel) {
  return getCardOptions({
    navigationLevel,
    isKeyword: true,
  });
}

function getSearchTermCardOptions(navigationLevel) {
  return getCardOptions({
    navigationLevel,
    isSearchTerm: true,
  });
}

function getCampaignLevelDataEntitySetOptions() {
  return getCardOptions({
    navigationLevel: 'campaign',
  });
}

export function getFluentCardsConfigs({
  permissions, showWelcomeTips = false, isMobileAppCampaign = false, campaignType,
}) {
  const audienceUnderspendShowTile = _.get(permissions, 'dynamic.AudienceUnderspendShowTile', false);

  const defaultHiddenCampaignTypes = [
    CampaignLevelCampaignType.None,
    CampaignLevelCampaignType.Default,
    CampaignLevelCampaignType.Shopping,
    CampaignLevelCampaignType.DynamicSearchAds,
    CampaignLevelCampaignType.Audience,
    CampaignLevelCampaignType.Smart,
    CampaignLevelCampaignType.Hotel,
    CampaignLevelCampaignType.App,
    CampaignLevelCampaignType.LinkedIn,
  ];

  const diagnosticCardDefaultHiddenCampaignTypesFiltered = audienceUnderspendShowTile ? _.without(defaultHiddenCampaignTypes, CampaignLevelCampaignType.Audience) : defaultHiddenCampaignTypes;
  const diagnosticCardForMobileAppCampaignsFiltered = diagnosticCardDefaultHiddenCampaignTypesFiltered.filter(type => type !== CampaignLevelCampaignType.App);

  const getRecommendationsCardObject = () => (
    {
      typeId: cardRecommendationsId,
      hideForCampaignType: [
        AccountLevelCampaignType.Hotel,
        AccountLevelCampaignType.App,
        AccountLevelCampaignType.LinkedIn,
        AccountLevelCampaignType.PerformanceMax,
        CampaignLevelCampaignType.Hotel,
        CampaignLevelCampaignType.App,
        CampaignLevelCampaignType.LinkedIn,
        CampaignLevelCampaignType.PerformanceMax,
      ],
      name: _TL_('Recommendations'),
      size: CardSize.Small,
      getEntitySetOptions: getCardOptions,
      enabled: true,
    }
  );

  const getDiagnosticCardObject = () => (
    {
      validFor: ['campaign'],
      typeId: cardDiagnosticId,
      hideForCampaignType: isMobileAppCampaign ? diagnosticCardForMobileAppCampaignsFiltered : diagnosticCardDefaultHiddenCampaignTypesFiltered,
      name: _TL_('Diagnostic'),
      size: CardSize.Large,
      dynamicHeightForNarrow: true,
      enabled: true,
    }
  );

  const cardsConfig = showWelcomeTips ? [
    {
      validFor: ['account'],
      typeId: cardWelcomeTipsId,
      name: _TL_('Tips'),
      size: CardSize.Small,
      getEntitySetOptions: getCardOptions,
      enabled: true,
    },
  ] :
    // Order matters in this array, as this will be the default order of the cards in the dashboard
    // When we need to add a new config here, please refer to old overview page to get the correct position in this array
    // If we need to add a new card, please check with PM/Designer to get the correct position
    [
      {
        typeId: cardPerformanceId,
        name: _TL_('Performance'),
        size: CardSize.Large,
        dynamicHeightForNarrow: true,
        getEntitySetOptions: getCardOptions,
        getDefaultPreference: getDefaultPerformanceChartPreference,
        isPreferenceValid: isPerformanceChartPreferenceValid,
        enabled: true,
      },
      campaignType === CampaignLevelCampaignType.Audience ? getDiagnosticCardObject() : getRecommendationsCardObject(),
      campaignType === CampaignLevelCampaignType.Audience ? getRecommendationsCardObject() : getDiagnosticCardObject(),
      {
        validFor: ['account'],
        typeId: cardTopCampaignsId,
        name: _TL_('Campaigns'),
        size: CardSize.Small,
        getEntitySetOptions: getAccountLevelDataEntitySetOptions,
        getDfmPreferenceObject: getDfmPreferenceObjectForPerformanceTable,
        getDefaultPreference: getDefaultPerformanceTablePreference,
        isPreferenceValid: isTablePreferenceValid,
        enabled: true,
      },
      {
        validFor: ['campaign'],
        typeId: cardTopAdGroupsId,
        hideForCampaignType: [
          CampaignLevelCampaignType.PerformanceMax,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
          CampaignLevelCampaignType.PerformanceMax,
        ],
        name: _TL_('Ad groups'),
        size: CardSize.Small,
        getEntitySetOptions: getCampaignLevelDataEntitySetOptions,
        // Ad group table will not have DFM, but we need it's preference to send out performance chart
        // aggregated data request, so we need to use getDfmPreferenceObject to convert new preference
        // so that data interface (processGridTileOptions, getColumnAndOrder) in
        // overview-cards-common/src/fetch.js can recognize it.
        getDfmPreferenceObject: getDfmPreferenceObjectForPerformanceTable,
        getDefaultPreference: getDefaultPerformanceTablePreference,
        isPreferenceValid: isTablePreferenceValid,
        enabled: true,
      },
      {
        validFor: ['customer'],
        typeId: cardTopChangesAccountId,
        name: _TL_('Top changes: Accounts'),
        getEntitySetOptions: getCustomerLevelDataEntitySetOptions,
        enabled: true,
        size: CardSize.Small,
      },
      {
        validFor: ['account'],
        typeId: cardTopChangesCampaignId,
        hideForCampaignType: [AccountLevelCampaignType.App],
        name: _TL_('Top changes: Campaigns'),
        getEntitySetOptions: getAccountLevelDataEntitySetOptions,
        getDfmPreferenceObject: getDfmPreferenceObjectForTopChanges,
        getDefaultPreference: getDefaultTopChangesPreference,
        enabled: true,
        size: CardSize.Small,
      },
      {
        validFor: ['campaign'],
        typeId: cardTopChangesAdGroupId,
        hideForCampaignType: [
          CampaignLevelCampaignType.PerformanceMax,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
          CampaignLevelCampaignType.PerformanceMax,
        ],
        name: _TL_('Top changes: Ad groups'),
        getEntitySetOptions: getCampaignLevelDataEntitySetOptions,
        enabled: true,
        size: CardSize.Small,
      },
      {
        validFor: ['customer'],
        typeId: cardTopAccountsId,
        name: _TL_('Accounts'),
        getEntitySetOptions: getCustomerLevelDataEntitySetOptions,
        getDfmPreferenceObject: getDfmPreferenceObjectForPerformanceTable,
        getDefaultPreference: getDefaultPerformanceTablePreference,
        isPreferenceValid: isTablePreferenceValid,
        enabled: true,
        size: CardSize.Small,
      },
      {
        validFor: ['account', 'campaign'],
        typeId: cardConversionTrackingId,
        hideForCampaignType: [
          AccountLevelCampaignType.App,
          CampaignLevelCampaignType.None,
          CampaignLevelCampaignType.Default,
          CampaignLevelCampaignType.Shopping,
          CampaignLevelCampaignType.DynamicSearchAds,
          CampaignLevelCampaignType.Audience,
          CampaignLevelCampaignType.Smart,
          CampaignLevelCampaignType.Hotel,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
          CampaignLevelCampaignType.PerformanceMax,
        ],
        hideForCampaignTypeSDK: [
          AccountLevelCampaignType.App,
          CampaignLevelCampaignType.None,
          CampaignLevelCampaignType.Default,
          CampaignLevelCampaignType.Shopping,
          CampaignLevelCampaignType.DynamicSearchAds,
          CampaignLevelCampaignType.Audience,
          CampaignLevelCampaignType.Smart,
          CampaignLevelCampaignType.Hotel,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
        ],
        name: _TL_('Conversion Tracking'),
        size: CardSize.Small,
        getEntitySetOptions: getAccountLevelDataEntitySetOptions,
        enabled: true,
      },
      {
        validFor: ['account', 'campaign'],
        typeId: cardTopKeywordsId,
        hideForCampaignType: [
          AccountLevelCampaignType.Hotel,
          AccountLevelCampaignType.PerformanceMax,
          AccountLevelCampaignType.App,
          AccountLevelCampaignType.LinkedIn,
          AccountLevelCampaignType.PerformanceMax,
          AccountLevelCampaignType.Audience,
          AccountLevelCampaignType.Shopping,
          CampaignLevelCampaignType.Hotel,
          CampaignLevelCampaignType.PerformanceMax,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
          CampaignLevelCampaignType.PerformanceMax,
          CampaignLevelCampaignType.Audience,
          CampaignLevelCampaignType.Shopping,
        ],
        name: _TL_('Keywords'),
        size: CardSize.Small,
        getEntitySetOptions: getTopKeywordCardOptions,
        getDfmPreferenceObject: getDfmPreferenceObjectForPerformanceTable,
        getDefaultPreference: getDefaultPerformanceTablePreference,
        isPreferenceValid: isTablePreferenceValid,
        enabled: true,
      },
      {
        validFor: ['account', 'campaign'],
        typeId: cardSearchTermsId,
        hideForCampaignType: [
          AccountLevelCampaignType.Hotel,
          AccountLevelCampaignType.PerformanceMax,
          AccountLevelCampaignType.App,
          AccountLevelCampaignType.LinkedIn,
          AccountLevelCampaignType.Audience,
          CampaignLevelCampaignType.Hotel,
          CampaignLevelCampaignType.PerformanceMax,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
          CampaignLevelCampaignType.Audience,
        ],
        name: _TL_('What people searched'),
        size: CardSize.Small,
        getEntitySetOptions: getSearchTermCardOptions,
        getDfmPreferenceObject: getDfmPreferenceObjectForPerformanceTable,
        getDefaultPreference: getDefaultPerformanceTablePreference,
        isPreferenceValid: isTablePreferenceValid,
        enabled: true,
      },
      {
        typeId: cardDeviceId,
        hideForCampaignType: [
          AccountLevelCampaignType.PerformanceMax,
          AccountLevelCampaignType.App,
          AccountLevelCampaignType.LinkedIn,
          CampaignLevelCampaignType.PerformanceMax,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
        ],
        name: _TL_('Devices'),
        size: CardSize.Small,
        getEntitySetOptions: getCardOptions,
        getDefaultPreference: getDefaultDevicePreference,
        isPreferenceValid: isDevicePreferenceValid,
        enabled: true,
      },
      {
        validFor: ['campaign'],
        typeId: cardTopLocationsId,
        hideForCampaignType: [
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
        ],
        name: _TL_('Top Locations'),
        size: CardSize.Small,
        getEntitySetOptions: getCardOptions,
        enabled: true,
      },
      {
        validFor: ['campaign'],
        typeId: cardAssetPerformanceId,
        hideForCampaignType: isMobileAppCampaign ? defaultHiddenCampaignTypes.filter(type => type !== CampaignLevelCampaignType.App) : defaultHiddenCampaignTypes,
        name: _TL_('Asset Performance'),
        size: CardSize.Small,
        enabled: true,
      },
      {
        validFor: ['account', 'campaign'],
        typeId: cardCompetitionId,
        name: _TL_('Competition'),
        size: CardSize.Small,
        hideForCampaignType: [
          AccountLevelCampaignType.Hotel,
          AccountLevelCampaignType.PerformanceMax,
          AccountLevelCampaignType.App,
          AccountLevelCampaignType.LinkedIn,
          CampaignLevelCampaignType.Hotel,
          CampaignLevelCampaignType.PerformanceMax,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
        ],
        getEntitySetOptions: getCardOptions,
        enabled: true,
      },
      {
        validFor: ['campaign'],
        typeId: cardNavigateToFullProductId,
        name: _TL_(''),
        size: CardSize.Small,
        enabled: true,
        hideForCampaignType: [
          CampaignLevelCampaignType.None,
          CampaignLevelCampaignType.Default,
          CampaignLevelCampaignType.Shopping,
          CampaignLevelCampaignType.DynamicSearchAds,
          CampaignLevelCampaignType.Audience,
          CampaignLevelCampaignType.Smart,
          CampaignLevelCampaignType.Hotel,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
          CampaignLevelCampaignType.PerformanceMax,
        ],
        hideForCampaignTypeSDK: [
          CampaignLevelCampaignType.None,
          CampaignLevelCampaignType.Default,
          CampaignLevelCampaignType.Shopping,
          CampaignLevelCampaignType.DynamicSearchAds,
          CampaignLevelCampaignType.Audience,
          CampaignLevelCampaignType.Smart,
          CampaignLevelCampaignType.Hotel,
          CampaignLevelCampaignType.App,
          CampaignLevelCampaignType.LinkedIn,
        ],
      },
    ];

  cardsConfig.isValidForNavigationLevel = (cardConfig, navigationLevel, campaignType, isSDK = false) =>
    ((_.isFunction(cardConfig.enabled) && cardConfig.enabled()) ||
      (_.isBoolean(cardConfig.enabled) && cardConfig.enabled)) &&
    (!cardConfig.validFor || _.contains(cardConfig.validFor, navigationLevel)) &&
    (((!isSDK || !cardConfig.hideForCampaignTypeSDK) &&
      (!cardConfig.hideForCampaignType || !_.contains(cardConfig.hideForCampaignType, campaignType)))
      || (isSDK && cardConfig.hideForCampaignTypeSDK && !_.contains(cardConfig.hideForCampaignTypeSDK, campaignType)));

  return cardsConfig;
}
